import { forwardRef } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cn } from '@/lib/utils'

/**
 * Popover primitive — shadcn/ui-compatible, Genesis tokens applied.
 *
 * Added in Story 10.1 for the sidebar user-card popover. Unlike a
 * DropdownMenu, a Popover does NOT close when you interact with its content,
 * which is essential: the theme segmented control inside must stay open while
 * the user clicks Claro/Escuro/Auto (AC-2). "Configurações"/"Sair"
 * close it explicitly via the controlled `open` state on the consumer side.
 *
 * ⚠️ Este docblock AFIRMAVA que a superfície era vidro e o código dizia
 * `bg-panel-solid … shadow-lg` — vidro nenhum. O comentário mentia, e um `grep`
 * por "glass" casava com ele e não com o código. Agora a afirmação é verdade:
 * a superfície é a `@utility glass-float`, a MESMA do DropdownMenu e do Select,
 * porque os três são a mesma coisa — chrome flutuante, o único plano onde o
 * `backdrop-filter` é permitido.
 *
 * O `glass-float` é AUTO-CONTIDO: traz tinta, lustro, blur, `border`,
 * `box-shadow` (aresta iluminada + lente + sombra de flutuação) e `position`.
 * Por isso o `className` aqui NÃO repete `border`/`border-hair`/`shadow-lg`/
 * `relative` — repeti-los era a origem das duas gramáticas de aresta que a
 * utility veio unificar. O `rounded-md` FICA: a utility não define raio de
 * propósito, cada overlay escolhe o seu.
 *
 * ⚠️ Não acrescentar `backdrop-blur-*`: o `glass-float` já declara
 * `backdrop-filter`, e duas declarações fazem o Lightning CSS servir
 * `backdrop-filter: none`.
 */
export const Popover = PopoverPrimitive.Root

export const PopoverTrigger = PopoverPrimitive.Trigger

export const PopoverAnchor = PopoverPrimitive.Anchor

/**
 * `portalContainer` — para onde o conteúdo flutuante é portado. Omitido (o caso
 * de quase todas as telas) mantém o default do Radix: `document.body`.
 *
 * Existe pela MESMA razão que o gémeo em `components/ui/select.tsx`, e a razão é
 * o `<dialog>` NATIVO de `components/ui/dialog.tsx`: um diálogo aberto com
 * `showModal()` vive na top layer do browser, e um portal para `document.body`
 * fica no documento normal — pinta POR BAIXO do backdrop. O popover abre, e não
 * se vê. Sem erro nenhum a apontar para aqui.
 *
 * Passando o nó do `DialogContent`, o conteúdo nasce dentro do próprio diálogo e
 * volta à top layer com ele. É o que permite ao `EntityCombobox` (que é
 * Popover + lista) ser usado dentro de um diálogo.
 */
type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  portalContainer?: HTMLElement | null
}

export const PopoverContent = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = 'center', sideOffset = 8, portalContainer, ...props }, ref) => (
  <PopoverPrimitive.Portal {...(portalContainer ? { container: portalContainer } : {})}>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'glass-float text-ink z-50 rounded-md p-2',
        // A entrada e a saída (`globals.css`, #502): cresce a partir do gatilho.
        // A origem na sintaxe da v4, `origin-(--…)` — a de parênteses retos da v3
        // gerava `transform-origin: --…`, inválido, e a escala partia do centro.
        'overlay-flutuante origin-(--radix-popover-content-transform-origin)',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName
